import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@/graphql/base-query";
import {
  GENERATE_CHAT_ATTACHMENT_SIGNED_URL,
  GENERATE_CHAT_ATTACHMENT_ACCESS_SIGNED_URL,
  CREATE_CHAT_ATTACHMENT,
  GET_CHAT_ATTACHMENTS,
  GET_CHAT_ATTACHMENT,
  DELETE_CHAT_ATTACHMENT,
  GENERATE_PENDING_ATTACHMENT_SIGNED_URL,
  CREATE_PENDING_ATTACHMENT,
  ASSOCIATE_PENDING_ATTACHMENTS,
} from "@/graphql/chat-requests";
import {
  ChatAttachment,
  CreateChatAttachmentInput,
  GenerateChatAttachmentSignedUrlResponse,
  CreateChatAttachmentResponse,
  GetChatAttachmentsResponse,
  GetChatAttachmentResponse,
  DeleteChatAttachmentResponse,
} from "@/types/chat";

export const chatAttachmentApi = createApi({
  reducerPath: "api/chatAttachment",
  baseQuery,
  tagTypes: ["ChatAttachment"],
  endpoints: (builder) => ({
    // Get signed URL for uploading a file
    generateChatAttachmentSignedUrl: builder.query<
      { signedUrl: string; key: string },
      { chatId: string; tenantId?: string; fileName: string; contentType: string; sizeBytes: number }
    >({
      query: ({ chatId, tenantId, fileName, contentType, sizeBytes }) => ({
        document: GENERATE_CHAT_ATTACHMENT_SIGNED_URL,
        variables: { chatId, tenantId, fileName, contentType, sizeBytes },
      }),
      transformResponse: (response: GenerateChatAttachmentSignedUrlResponse) =>
        response.generateChatAttachmentUploadSignedUrl,
    }),

    // Create attachment record after upload
    createChatAttachment: builder.mutation<ChatAttachment, CreateChatAttachmentInput>({
      query: (input) => ({
        document: CREATE_CHAT_ATTACHMENT,
        variables: { input },
      }),
      invalidatesTags: (_, __, { chatId }) => [
        { type: "ChatAttachment", id: chatId },
      ],
      transformResponse: (response: CreateChatAttachmentResponse) =>
        response.createChatAttachment.data,
    }),

    // Get all attachments for a chat
    getChatAttachments: builder.query<ChatAttachment[], string>({
      query: (chatId) => ({
        document: GET_CHAT_ATTACHMENTS,
        variables: { chatId },
      }),
      providesTags: (_, __, chatId) => [{ type: "ChatAttachment", id: chatId }],
      transformResponse: (response: GetChatAttachmentsResponse) =>
        response.chatAttachments.data,
    }),

    // Get signed URL for viewing/downloading an attachment (e.g., image preview)
    generateChatAttachmentAccessSignedUrl: builder.query<
      { signedUrl: string; key: string },
      { id: string }
    >({
      query: ({ id }) => ({
        document: GENERATE_CHAT_ATTACHMENT_ACCESS_SIGNED_URL,
        variables: { id },
      }),
      transformResponse: (response: any) =>
        response.generateChatAttachmentAccessSignedUrl,
    }),

    // Get single attachment by ID
    getChatAttachment: builder.query<ChatAttachment, string>({
      query: (id) => ({
        document: GET_CHAT_ATTACHMENT,
        variables: { id },
      }),
      providesTags: (result) =>
        result ? [{ type: "ChatAttachment", id: result._id }] : [],
      transformResponse: (response: GetChatAttachmentResponse) =>
        response.chatAttachment.data,
    }),

    // Delete attachment
    deleteChatAttachment: builder.mutation<boolean, { id: string; chatId?: string }>({
      query: ({ id }) => ({
        document: DELETE_CHAT_ATTACHMENT,
        variables: { id },
      }),
      invalidatesTags: (_, __, { chatId }) =>
        chatId ? [{ type: "ChatAttachment", id: chatId }] : [],
      transformResponse: (response: DeleteChatAttachmentResponse) =>
        response.deleteChatAttachment.success,
    }),

    // Generate signed URL for pending attachment (before chat exists)
    generatePendingAttachmentSignedUrl: builder.query<
      { signedUrl: string; key: string },
      { sessionId: string; tenantId?: string; fileName: string; contentType: string; sizeBytes: number }
    >({
      query: ({ sessionId, tenantId, fileName, contentType, sizeBytes }) => ({
        document: GENERATE_PENDING_ATTACHMENT_SIGNED_URL,
        variables: { sessionId, tenantId, fileName, contentType, sizeBytes },
      }),
      transformResponse: (response: any) =>
        response.generatePendingAttachmentUploadSignedUrl,
    }),

    // Create pending attachment record
    createPendingAttachment: builder.mutation<
      ChatAttachment,
      { sessionId: string; tenantId?: string; s3Key: string; filename: string; mimeType: string; sizeBytes: number }
    >({
      query: (input) => ({
        document: CREATE_PENDING_ATTACHMENT,
        variables: { input },
      }),
      transformResponse: (response: any) =>
        response.createPendingAttachment.data,
    }),

    // Associate pending attachments with a chat
    associatePendingAttachments: builder.mutation<
      ChatAttachment[],
      { chatId: string; attachmentIds: string[] }
    >({
      query: ({ chatId, attachmentIds }) => ({
        document: ASSOCIATE_PENDING_ATTACHMENTS,
        variables: { chatId, attachmentIds },
      }),
      invalidatesTags: (_, __, { chatId }) => [
        { type: "ChatAttachment", id: chatId },
      ],
      transformResponse: (response: any) =>
        response.associatePendingAttachments.data,
    }),
  }),
});

export const {
  useLazyGenerateChatAttachmentSignedUrlQuery,
  useLazyGenerateChatAttachmentAccessSignedUrlQuery,
  useCreateChatAttachmentMutation,
  useGetChatAttachmentsQuery,
  useGetChatAttachmentQuery,
  useLazyGetChatAttachmentQuery,
  useDeleteChatAttachmentMutation,
  useLazyGeneratePendingAttachmentSignedUrlQuery,
  useCreatePendingAttachmentMutation,
  useAssociatePendingAttachmentsMutation,
} = chatAttachmentApi;

